import type { SlotChamadaStatusOperacional } from '../../components/chamada';
import type { TurnoAoVivoModel } from './useTurnoAoVivo';
import './turno-ao-vivo.css';

const STATUS_SHORT: Record<SlotChamadaStatusOperacional, string> = {
  registrada: 'OK',
  emCurso: 'Aberta',
  janela: 'Agora',
  perdida: 'Perdida',
  futuro: '—',
};

type Props = {
  model: TurnoAoVivoModel;
  horaAtual: string;
  portariaCount?: number;
  title?: string;
  onOpenGovernanca?: () => void;
  onOpenPortaria?: () => void;
};

export function TurnoAoVivoPanel({
  model,
  horaAtual,
  portariaCount,
  title = 'Turno ao vivo',
  onOpenGovernanca,
  onOpenPortaria,
}: Props) {
  const { kpis, rows, numerosAula, turnoLabel, aberturaHm, fechamentoHm, agoraPct, isLoading } =
    model;

  return (
    <div className="tav-panel" data-tour="page.turno-ao-vivo">
      <div className="tav-head">
        <div>
          <h3>
            {title}
            {turnoLabel ? (
              <span className="tav-turno-badge">{turnoLabel}</span>
            ) : null}
          </h3>
          <p className="tav-sub">
            {aberturaHm && fechamentoHm
              ? `Portaria ${aberturaHm} → encerramento ${fechamentoHm}`
              : 'Grade do turno vigente'}
            {typeof portariaCount === 'number'
              ? ` · Portaria hoje: ${portariaCount}`
              : ''}
            {` · Relógio ${horaAtual}`}
          </p>
        </div>
        <div className="tav-head-actions">
          {onOpenPortaria ? (
            <button type="button" className="tav-link" onClick={onOpenPortaria}>
              Portaria →
            </button>
          ) : null}
          {onOpenGovernanca ? (
            <button type="button" className="tav-link" onClick={onOpenGovernanca}>
              Governança →
            </button>
          ) : null}
        </div>
      </div>

      {aberturaHm && fechamentoHm ? (
        <div className="tav-ruler" aria-label="Progresso do turno">
          <span className="tav-ruler-hm">{aberturaHm}</span>
          <div className="tav-ruler-track">
            <div
              className="tav-ruler-fill"
              style={{ width: `${Math.min(100, Math.max(0, agoraPct))}%` }}
            />
            <div
              className="tav-ruler-now"
              style={{ left: `${Math.min(100, Math.max(0, agoraPct))}%` }}
              title={`Agora ${horaAtual}`}
            />
          </div>
          <span className="tav-ruler-hm">{fechamentoHm}</span>
        </div>
      ) : null}

      <div className="tav-kpis" aria-label="Indicadores do turno">
        <div className="tav-kpi">
          <span>Previstas</span>
          <strong>{kpis.previstas}</strong>
        </div>
        <div className="tav-kpi is-ok">
          <span>Ok</span>
          <strong>{kpis.ok}</strong>
        </div>
        <div className="tav-kpi is-mid">
          <span>Em janela</span>
          <strong>{kpis.emJanela + kpis.emCurso}</strong>
        </div>
        <div className={`tav-kpi${kpis.perdidas > 0 ? ' is-hot' : ''}`}>
          <span>Perdidas</span>
          <strong>{kpis.perdidas}</strong>
        </div>
        <div className="tav-kpi">
          <span>Aderência</span>
          <strong>
            {kpis.aderenciaPct != null ? `${kpis.aderenciaPct}%` : '—'}
          </strong>
        </div>
      </div>

      {isLoading && rows.length === 0 ? (
        <p className="tav-empty">Carregando grade do turno…</p>
      ) : rows.length === 0 ? (
        <p className="tav-empty">
          {turnoLabel
            ? `Nenhuma aula na grade do turno ${turnoLabel} hoje.`
            : 'Sem turno operacional ativo no relógio.'}
        </p>
      ) : (
        <div className="tav-scroll">
          <table className="tav-grid">
            <thead>
              <tr>
                <th className="tav-col-turma">Turma</th>
                {numerosAula.map((n) => (
                  <th key={n}>{n}ª</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.turmaId}>
                  <td className="tav-col-turma">
                    <div className="tav-turma-name">{row.turmaNome}</div>
                    <div className="tav-turma-meta">
                      {row.semNenhuma
                        ? `${row.perdidas} aula(s) sem chamada`
                        : row.parcial
                          ? `${row.ok} ok · faltam ${row.perdidas}`
                          : row.ok > 0
                            ? 'Em dia'
                            : 'No horário'}
                    </div>
                  </td>
                  {numerosAula.map((n) => {
                    const cell = row.cells.get(n);
                    if (!cell) {
                      return (
                        <td key={n} className="tav-cell is-empty">
                          <span>·</span>
                        </td>
                      );
                    }
                    return (
                      <td
                        key={n}
                        className={`tav-cell is-${cell.status}`}
                        title={cell.title}
                      >
                        <span className="tav-cell-st">
                          {STATUS_SHORT[cell.status]}
                        </span>
                        {cell.materiaNome ? (
                          <span className="tav-cell-mat">{cell.materiaNome}</span>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
